import { Check, ChevronRight, X, AlertTriangle } from 'lucide-react';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { cn } from '@reflex/ui/lib/utils';
import { formatDurationPrecise } from '@reflex/ui/lib/format';
import { McpServerRow } from './McpServerRow';
import { SetupLogPanel } from './SetupLogPanel';
import type { LogsState, SetupStepInfo } from './types';

interface SetupStepRowProps {
  step: SetupStepInfo;
  isDone: boolean;
  isActive: boolean;
  isExpanded: boolean;
  expandedMcpServers: Set<string>;
  logs: LogsState | undefined;
  interactive: boolean;
  onToggleStep: (stepId: string, hasSubRows: boolean) => void;
  onToggleMcpServer: (stepId: string, serverName: string) => void;
}

export function SetupStepRow({
  step,
  isDone,
  isActive,
  isExpanded,
  expandedMcpServers,
  logs,
  interactive,
  onToggleStep,
  onToggleMcpServer,
}: SetupStepRowProps) {
  const Icon = step.icon;
  const mcpServers = step.mcpDetail?.servers ?? [];
  const hasSubRows = mcpServers.length > 0;
  // The step itself completed, but a server it lists did not register or was
  // skipped; say so at the step level instead of only inside the sub-rows.
  const hasDegradedSubRows = mcpServers.some((s) => s.status !== 'ok');

  const rowContent = (
    <>
      {isDone && step.failed ? (
        <X className="h-3 w-3 shrink-0 text-status-error" />
      ) : isDone && hasDegradedSubRows ? (
        <AlertTriangle className="h-3 w-3 shrink-0 text-status-warning" />
      ) : isDone ? (
        <Check className="h-3 w-3 shrink-0 text-status-success" />
      ) : isActive ? (
        <Spinner size="xs" className="text-status-info" />
      ) : (
        <Icon className="h-3 w-3 shrink-0 text-muted-foreground/30" />
      )}
      <span className={isActive ? 'text-foreground' : 'text-muted-foreground'}>{step.label}</span>
      {isDone && step.detail && (
        <span
          className={cn(
            'truncate text-3xs',
            step.failed
              ? 'text-status-error'
              : hasDegradedSubRows
                ? 'text-status-warning'
                : 'text-muted-foreground',
          )}
        >
          {step.detail}
        </span>
      )}
      {isDone && step.durationMs != null && (
        <span className="ml-auto shrink-0 text-3xs tabular-nums text-muted-foreground/70">
          {formatDurationPrecise(step.durationMs)}
        </span>
      )}
    </>
  );

  const stepStatus = isDone ? (step.failed ? 'failed' : 'done') : isActive ? 'active' : 'pending';

  return (
    <div
      data-testid={`setup-step-${step.id}`}
      data-status={stepStatus}
      data-degraded={hasDegradedSubRows || undefined}
      data-blocking={step.blocking ? 'true' : undefined}
      className="flex flex-col gap-1"
    >
      {interactive ? (
        <button
          type="button"
          onClick={() => onToggleStep(step.id, hasSubRows)}
          aria-expanded={isExpanded}
          aria-label={
            hasSubRows
              ? `${isExpanded ? 'Hide' : 'Show'} servers for ${step.label}`
              : `${isExpanded ? 'Hide' : 'View'} logs for ${step.label}`
          }
          className="group -mx-1 flex items-center gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-muted/50"
        >
          {rowContent}
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform duration-200 group-hover:text-muted-foreground',
              !(isDone && step.durationMs != null) && 'ml-auto',
              isExpanded && 'rotate-90',
            )}
          />
        </button>
      ) : (
        <div className="flex items-center gap-2 text-xs">{rowContent}</div>
      )}
      <AnimatedCollapse open={isExpanded}>
        {hasSubRows ? (
          <div className="ml-5 flex flex-col gap-1">
            {mcpServers.map((server) => (
              <McpServerRow
                key={server.name}
                stepId={step.id}
                server={server}
                expanded={expandedMcpServers.has(`${step.id}::${server.name}`)}
                interactive={interactive}
                logs={logs}
                onToggle={() => onToggleMcpServer(step.id, server.name)}
              />
            ))}
          </div>
        ) : (
          <div className="ml-5 rounded-lg border border-border/30 bg-card/50 p-2">
            <SetupLogPanel logs={logs} />
          </div>
        )}
      </AnimatedCollapse>
    </div>
  );
}
